// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMedplum } from '@medplum/react';
import { type JSX, useState } from 'react';
import { botIdentifier } from '../../lib/bot-identifier';
import { showErrorNotification } from '../../utils/notifications';

interface DisableProviderModalProps {
  opened: boolean;
  onClose: () => void;
  practitioner: {
    id: string;
    name: string;
    email: string;
  };
  onSuccess: () => void;
}

/**
 * Confirmation modal for disabling an active provider.
 *
 * Sets ProjectMembership.active = false via the disable-provider bot.
 * No irreversible-action checkbox — the action is reversible (Enable restores access).
 */
export function DisableProviderModal({
  opened,
  onClose,
  practitioner,
  onSuccess,
}: DisableProviderModalProps): JSX.Element {
  const medplum = useMedplum();
  const [loading, setLoading] = useState(false);

  const handleClose = (): void => {
    onClose();
  };

  const handleConfirm = async (): Promise<void> => {
    setLoading(true);
    try {
      await medplum.executeBot(botIdentifier('disable-provider'), {
        practitionerReference: `Practitioner/${practitioner.id}`,
      });
      notifications.show({
        color: 'green',
        title: 'Provider disabled',
        message: `${practitioner.name} has been disabled and can no longer sign in.`,
      });
      onSuccess();
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Disable provider"
      size="md"
      data-testid="disable-provider-modal"
    >
      <Stack gap="md">
        <Text size="sm">
          Disable{' '}
          <Text component="span" fw={600}>
            {practitioner.name}
          </Text>{' '}
          (
          <Text component="span" fs="italic">
            {practitioner.email}
          </Text>
          )? They will not be able to sign in to the provider portal until re-enabled.
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button
            variant="default"
            onClick={handleClose}
            disabled={loading}
            data-testid="disable-provider-cancel-button"
          >
            Cancel
          </Button>
          <Button
            color="red"
            loading={loading}
            onClick={() => {
              handleConfirm().catch(console.error);
            }}
            data-testid="disable-provider-confirm-button"
          >
            Disable provider
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
